import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Card } from '@codes/ui-kit/components/ui/card';
import { Image } from '@codes/ui-kit/components/ui/image';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@codes/ui-kit/components/ui/select';
import { toast } from '@codes/ui-kit/components/ui/use-toast';
import { SaveSecretBody, WorkspaceView } from '@lemoncloud/codes-goods-api';
import { useProjectService, useWorkspaceService } from '@web/app/features/home/hooks';
import { useWorkspaceSettingDialog } from '@web/app/features/home/hooks/useWorkspaceSettingDialog';
import { useSelectedStore, useUpdateQueryData } from '@web/app/shared';
import { Check, ChevronLeft, ChevronRight, Info, X } from 'lucide-react';

import { KeyInfoDarkImage, KeyInfoImage } from '@codes/assets';
import { useRegions } from '@codes/cloud';
import { useOwnerStore } from '@codes/owner';
import { useProjects } from '@codes/project';
import { PaginationType } from '@codes/shared';
import {
    Button,
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    Input,
    Label,
    PageDialog,
    PageDialogClose,
    PageDialogContent,
    PageDialogHeader,
    PageDialogTitle,
    Separator,
    SharedSpinner,
    Sheet,
    SheetClose,
    SheetContent,
    SheetHeader,
} from '@codes/ui-kit';
import {
    useDeleteWorkspace,
    useEnrollAccessKeyWithWorkspaceId,
    useUpdateWorkspace,
    workspacesKeys,
} from '@codes/workspace';

const DEFAULT_SECRET_BODY: SaveSecretBody = {
    accessKey: '',
    secretKey: '',
};

export const WorkspaceSettingDialog = () => {
    const { isOpen, workspace, close } = useWorkspaceSettingDialog();

    const { setWorkspace, setProject } = useSelectedStore();
    const { workspaceFromServer, isOwner } = useWorkspaceService();
    const { areMyProjectsEmpty, myProjectsQueryResult } = useProjectService();

    const updateWorkspaceQueryData = useUpdateQueryData<PaginationType<WorkspaceView[]>>();
    const { owner } = useOwnerStore();

    const { t } = useTranslation();

    const { mutateAsync: updateWorkspace, data: updatedWorkspace, isPending: isUpdating } = useUpdateWorkspace();
    const { mutateAsync: deleteWorkspace, data: deletedWorkspace, isPending: isDeleting } = useDeleteWorkspace();

    const {
        mutateAsync: enrollAccessKeyWithWorkspaceId,
        data: workspaceWithSecret,
        isPending: isAccessKeyEnrolling,
        reset: resetWorkspaceWithSecret,
        isError: isEnrollAccessKeyError,
    } = useEnrollAccessKeyWithWorkspaceId();

    const haveAccessKey = !!workspace?.secret$ && !!workspace.secret$.accountNo;
    const { data: projectsData } = useProjects(workspace?.id as string, { limit: 50, page: 0 });
    const hasNoProject = projectsData?.total === 0;
    const { data: regions } = useRegions({ limit: 100, cloudId: workspace?.cloudId });

    const [workspaceName, setWorkspaceName] = useState(workspace?.name || '');
    const [workspaceRegionId, setWorkspaceRegionId] = useState(workspace?.regionId || '');
    const [isKeyInfoOpen, setIsKeyInfoOpen] = useState(false);
    const [isKeyFormOpen, setIsKeyFormOpen] = useState(false);
    const [saveSecretBody, setSaveSecretBody] = useState<SaveSecretBody>({ ...DEFAULT_SECRET_BODY });
    const [accessKeyEditMode, setAccessKeyEditMode] = useState(false);

    const isWorkspaceInputDisabled = workspaceName === workspace?.name || !workspaceName || isUpdating;
    const isUpdateRegionBtnDisabled = workspaceRegionId === workspace?.regionId || !workspaceRegionId || isUpdating;
    const isUpdateAccessBtnDisabled = !saveSecretBody.accessKey || !saveSecretBody.secretKey || isAccessKeyEnrolling;

    const handleUpdateWorkspaceNameBtnClick = () => {
        if (!workspace) {
            return;
        }
        updateWorkspace({ ...workspace, workspaceId: workspace.id as string, name: workspaceName });
    };

    const handleUpdateWorkspaceRegionBtnClick = () => {
        if (!workspace) {
            return;
        }
        updateWorkspace({ workspaceId: workspace.id as string, regionId: workspaceRegionId });
    };

    const handleDeleteWorkspaceBtnClick = () => {
        if (!workspace || !owner || !!myProjectsQueryResult.error) {
            return;
        }

        const isDefault = workspace.id === owner.defWorkspaceId;
        if (isDefault) {
            toast({ description: t('workspace.setting.deleteDefaultWorkspaceNotAllowed') });
            return;
        }

        if (!isOwner) {
            toast({ description: t('workspace.setting.deleteWorkspaceNotAllowedForNonOwner') });
            return;
        }

        if (!areMyProjectsEmpty) {
            toast({ description: t('workspace.setting.workspaceDeletionNotAllowedDueToProjects') });
            return;
        }

        const isOkay = confirm(t('workspace.setting.confirmToDeleteWorkspace'));
        if (!isOkay) {
            return;
        }

        deleteWorkspace(workspace.id as string);
    };

    const handleCompleteToUpdateWorkspace = (updatedWorkspace: WorkspaceView) => {
        setWorkspace(updatedWorkspace);
        toast({ description: t('workspace.setting.completeToUpdateWorkspace') });

        updateWorkspaceQueryData({
            queryKey: workspacesKeys.list({}),
            updater: oldData => {
                if (!oldData) {
                    return { total: 1, data: [updatedWorkspace], list: [updatedWorkspace], page: 0 };
                }

                const updatedData = oldData.data.map(oldWorkspace => {
                    if (oldWorkspace.id === updatedWorkspace.id) {
                        return updatedWorkspace;
                    }
                    return oldWorkspace;
                });

                return {
                    ...oldData,
                    data: updatedData,
                    list: updatedData,
                };
            },
        });
    };

    const handleCompleteToDeleteWorkspace = (deletedWorkspace: WorkspaceView) => {
        setWorkspace(null);
        setProject(null);
        toast({ description: t('workspace.setting.completeToDeleteWorkspace') });

        updateWorkspaceQueryData({
            queryKey: workspacesKeys.list({}),
            updater: oldData => {
                if (!oldData) {
                    return { total: 0, data: [], list: [], page: 0 };
                }

                const updatedData = oldData.data.filter(oldWorkspace => {
                    return oldWorkspace.id !== deletedWorkspace.id;
                });

                return {
                    ...oldData,
                    data: updatedData,
                    list: updatedData,
                    total: (oldData.total || 1) - 1,
                };
            },
        });

        close();
    };

    const projectsNameLabel = useMemo(() => {
        if (!projectsData || !projectsData.data || projectsData.data.length === 0) {
            return null;
        }
        const project = projectsData.data[0];
        const projectsCount = projectsData.total;

        if (projectsCount === 1) {
            return <div className="text-base text-sub-title font-medium">{project.name}</div>;
        }

        return (
            <div className="text-base text-sub-title font-medium">
                {t('project.projectCountLabel', { projectName: project.name, count: (projectsCount || 1) - 1 })}
            </div>
        );
    }, [projectsData]);

    const enrollAccessKeyBtnTapped = () => {
        if (isUpdateAccessBtnDisabled) {
            return;
        }
        if (workspaceWithSecret) {
            setIsKeyFormOpen(false);
            return;
        }
        if (!workspace || !workspace.id) {
            return;
        }
        enrollAccessKeyWithWorkspaceId({ body: saveSecretBody, workspaceId: workspace.id });
    };

    const enterAccessKeyBtnTapped = () => {
        setIsKeyFormOpen(true);
        setAccessKeyEditMode(!haveAccessKey);
    };

    useEffect(() => {
        if (!workspace) {
            return;
        }
        setWorkspaceName(workspace.name ?? '');
        setWorkspaceRegionId(workspace.regionId ?? '');
    }, [workspace]);

    useEffect(() => {
        if (!updatedWorkspace) {
            return;
        }
        handleCompleteToUpdateWorkspace(updatedWorkspace);
    }, [updatedWorkspace]);

    useEffect(() => {
        if (!deletedWorkspace) {
            return;
        }
        handleCompleteToDeleteWorkspace(deletedWorkspace);
    }, [deletedWorkspace]);

    useEffect(() => {
        if (isKeyFormOpen) {
            setSaveSecretBody({ ...DEFAULT_SECRET_BODY });
            return;
        }
        resetWorkspaceWithSecret();
    }, [isKeyFormOpen]);

    useEffect(() => {
        if (!workspaceWithSecret) {
            return;
        }
        setWorkspace(workspaceWithSecret);
    }, [workspaceWithSecret]);

    return (
        <PageDialog open={isOpen} onOpenChange={open => !open && close()}>
            <PageDialogContent
                showCloseButton={false}
                onInteractOutside={e => e.preventDefault()}
                onEscapeKeyDown={e => e.preventDefault()}
            >
                <div className="w-full max-w-[500px] sm:pt-[56px] sm:pb-[34px]">
                    <PageDialogHeader>
                        <PageDialogTitle>{t('workspace.setting.title')}</PageDialogTitle>
                        <PageDialogClose asChild>
                            <button className="absolute left-6 top-[19px] z-[51]" type="button">
                                <ChevronLeft className="w-5 h-5" />
                            </button>
                        </PageDialogClose>
                    </PageDialogHeader>
                    <div className="overflow-auto px-6 space-y-7 mt-5">
                        <Card className="py-6 px-5 rounded-2xl">
                            <Label variant="form" className="text-base">
                                {t('workspace.setting.labelText')}
                            </Label>
                            <div className="flex flex-col gap-y-[22px]">
                                <Input
                                    type="text"
                                    value={workspaceName}
                                    onChange={e => setWorkspaceName(e.target.value)}
                                />
                                <Button
                                    size="lg"
                                    disabled={isWorkspaceInputDisabled}
                                    onClick={handleUpdateWorkspaceNameBtnClick}
                                >
                                    {t('project.edit')}
                                </Button>
                            </div>
                        </Card>

                        <Card className="py-6 px-5 rounded-2xl">
                            <Label variant="form" className="text-base">
                                {t('workspace.setting.regionLabelText')}
                            </Label>
                            <div className="flex flex-col gap-y-[22px]">
                                <Select
                                    defaultValue={workspace?.region$?.id}
                                    value={workspaceRegionId}
                                    onValueChange={regionId => setWorkspaceRegionId(regionId)}
                                    disabled={!hasNoProject}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {regions?.list.map(region => (
                                            <SelectItem key={region.id} value={region.id as string}>
                                                {region.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <Button
                                    size="lg"
                                    disabled={isUpdateRegionBtnDisabled}
                                    onClick={handleUpdateWorkspaceRegionBtnClick}
                                >
                                    {t('project.edit')}
                                </Button>
                            </div>
                        </Card>
                        {workspace?.stereo !== 'basic' && (
                            <Card className="py-6 px-5 rounded-2xl">
                                <Label variant="form" className="text-base">
                                    {t('workspace.setting.accessKeyLabelText')}
                                </Label>
                                <button
                                    onClick={enterAccessKeyBtnTapped}
                                    className="w-full h-[41px] px-4 py-3 border border-border rounded-lg flex items-center justify-between"
                                >
                                    <div
                                        className={`text-sm flex items-center gap-1.5 font-medium ${
                                            haveAccessKey ? 'opacity-100' : 'opacity-40'
                                        }`}
                                    >
                                        <Check className="w-5 h-5" />
                                        {t('workspace.setting.accessKeyPlaceholderText')}
                                    </div>
                                    <ChevronRight className="w-5 h-5" />
                                </button>
                            </Card>
                        )}

                        <Card className="flex flex-col gap-y-[22px] py-6 px-5 rounded-2xl">
                            <div className="flex flex-col">
                                <Label className="text-text font-semibold text-base">
                                    {t('workspace.setting.workspaceDeletionLabelText')}
                                </Label>
                                <p className="text-sm text-error mt-[10px] font-medium">
                                    {t('workspace.setting.workspaceDeletionContentText')}
                                </p>
                            </div>
                            <Separator className="bg-default" />
                            <Card className="flex flex-col bg-box gap-y-1 rounded-xl">
                                <div className="flex items-center gap-2.5">
                                    <div className="text-lg font-medium truncate">{workspace?.name || ''}</div>
                                </div>
                                {projectsNameLabel}
                                <div className="flex justify-end">
                                    <button
                                        className="text-error font-medium"
                                        onClick={handleDeleteWorkspaceBtnClick}
                                        disabled={isDeleting}
                                    >
                                        {t('project.delete')}
                                    </button>
                                </div>
                            </Card>
                        </Card>
                    </div>
                    <Dialog open={isKeyFormOpen} onOpenChange={setIsKeyFormOpen}>
                        <DialogContent className="p-0" aria-describedby={undefined}>
                            <DialogHeader>
                                <DialogTitle>{t('workspace.create.accessKeyEnterButtonText')}</DialogTitle>
                            </DialogHeader>
                            <div className="py-5 px-4">
                                <Label variant="form" className="flex items-center gap-1">
                                    {t('workspace.create.manualAccessKeyLabelText')}
                                    <button onClick={() => setIsKeyInfoOpen(true)}>
                                        <Info className="w-[14px] h-[14px] text-placeholder" />
                                    </button>
                                </Label>
                                <Input
                                    disabled={!accessKeyEditMode}
                                    value={
                                        accessKeyEditMode
                                            ? saveSecretBody.accessKey
                                            : workspaceFromServer?.secret$?.['accessKey']
                                    }
                                    onChange={e => setSaveSecretBody(prev => ({ ...prev, accessKey: e.target.value }))}
                                    placeholder={t('workspace.create.enterSecretAccessKey')}
                                    className={isEnrollAccessKeyError ? 'border-error' : ''}
                                />
                                <div className="my-9">
                                    <Label variant="form">{t('workspace.create.secretAccessKeyLabelText')}</Label>
                                    <Input
                                        disabled={!accessKeyEditMode}
                                        value={
                                            accessKeyEditMode
                                                ? saveSecretBody.secretKey
                                                : workspaceFromServer?.secret$?.['decryptKey']
                                        }
                                        type="password"
                                        onChange={e =>
                                            setSaveSecretBody(prev => ({ ...prev, secretKey: e.target.value }))
                                        }
                                        placeholder={t('workspace.create.enterSecretAccessKey')}
                                        className={isEnrollAccessKeyError ? 'border-error' : ''}
                                    />
                                </div>
                                {isEnrollAccessKeyError && (
                                    <div className="mb-10 text-center text-error">
                                        {t('accessKey.errorMessageText')}
                                    </div>
                                )}
                                {workspaceWithSecret && (
                                    <div className="relative mt-5 mb-10">
                                        <Label variant="form">Account ID</Label>
                                        <Input value={workspaceWithSecret?.$secret?.accountNo} />
                                        <div className="absolute -top-8 h-8 py-1 px-2 rounded-md text-white bg-text inline">
                                            <div
                                                className="absolute bottom-[-6px] left-1/2 transform -translate-x-1/2 w-0 h-0
                        border-l-[5px] border-r-[5px] border-t-[7px] border-t-[#081837] border-transparent"
                                            ></div>
                                            {t('workspace.create.checkId')}
                                        </div>
                                    </div>
                                )}
                                {accessKeyEditMode ? (
                                    <Button
                                        size="lg"
                                        className="w-full"
                                        onClick={enrollAccessKeyBtnTapped}
                                        disabled={isUpdateAccessBtnDisabled}
                                    >
                                        {isAccessKeyEnrolling ? (
                                            <SharedSpinner />
                                        ) : (
                                            t(
                                                workspaceWithSecret
                                                    ? 'workspace.create.complete'
                                                    : 'workspace.create.authenticateKey'
                                            )
                                        )}
                                    </Button>
                                ) : (
                                    <Button size="lg" className="w-full" onClick={() => setAccessKeyEditMode(true)}>
                                        {t('accessKey.editButtonText')}
                                    </Button>
                                )}
                            </div>
                        </DialogContent>
                    </Dialog>

                    <Sheet open={isKeyInfoOpen} onOpenChange={setIsKeyInfoOpen}>
                        <SheetContent className="overflow-auto" aria-describedby={undefined}>
                            <SheetHeader className="justify-end">
                                <SheetClose>
                                    <X className="w-5 h-5" />
                                </SheetClose>
                            </SheetHeader>
                            <Image className="w-full mt-5" src={KeyInfoImage} darkSrc={KeyInfoDarkImage} />
                        </SheetContent>
                    </Sheet>
                </div>
            </PageDialogContent>
        </PageDialog>
    );
};
